<template>
  <section
    v-show="shapes.length"
    class="sli-poly-map"
    :class="{ 'is-flush': flush, 'is-fill': fill }"
  >
    <header
      v-if="title"
      class="sli-poly-map__head"
    >
      {{ title }}
    </header>
    <p
      v-if="error"
      class="sli-poly-map__error"
    >
      {{ error }}
    </p>
    <div
      ref="mapEl"
      class="sli-poly-map__canvas"
    />
  </section>
</template>

<style scoped>
.sli-poly-map {
  width: 100%;
  margin: 0;
  border-top: 1px solid rgba(6, 40, 77, 0.1);
}
.sli-poly-map.is-flush {
  border-top: 0;
}
.sli-poly-map.is-fill .sli-poly-map__canvas {
  height: min(62vh, 560px);
}
.sli-poly-map__head {
  padding: 8px 12px 4px;
  font-weight: 700;
  font-size: 0.85rem;
  color: #06284D;
}
.sli-poly-map__canvas {
  height: min(48vh, 420px);
  width: 100%;
  background: #e8eef5;
}
.sli-poly-map__error {
  color: #b42318;
  font-weight: 600;
  margin: 0;
  padding: 8px 12px;
}
@media (max-width: 767.98px) {
  .sli-poly-map__canvas {
    height: min(42vh, 280px);
  }
}
</style>

<script>
import { loadLeafletAssets } from '@/utils/leafletLoader'
import { BRAND_NAVY, BRAND_LIME } from '@/libs/brandColors'
import { coordinatesToLatLngs } from '../formModel'

export default {
  name: 'SliPolygonsMap',
  props: {
    locations: { type: Array, default: () => [] },
    title: { type: String, default: 'الخريطة' },
    flush: { type: Boolean, default: false },
    fill: { type: Boolean, default: false },
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      map: null,
      layer: null,
      error: '',
    }
  },
  computed: {
    shapes() {
      return (this.locations || []).map((land, index) => {
        const latlngs = coordinatesToLatLngs(land && land.coordinates)
        if (latlngs.length < 3) return null
        return {
          key: land.id || land._key || index,
          label: land.land_number || land.parcel_number || land.plot_number || `قطعة ${index + 1}`,
          latlngs,
          color: index % 2 === 0 ? BRAND_NAVY : BRAND_LIME,
        }
      }).filter(Boolean)
    },
    signature() {
      return this.shapes.map(shape => `${shape.key}:${shape.latlngs.length}`).join('|')
    },
  },
  watch: {
    signature() {
      if (this.active) this.renderMap()
    },
    active(value) {
      if (value) this.$nextTick(() => this.renderMap())
      else this.destroyMap()
    },
  },
  mounted() {
    if (this.active) this.renderMap()
  },
  beforeDestroy() {
    this.destroyMap()
  },
  methods: {
    destroyMap() {
      if (this.map) {
        this.map.remove()
        this.map = null
        this.layer = null
      }
    },
    async renderMap() {
      this.error = ''
      if (!this.shapes.length) {
        this.destroyMap()
        return
      }
      try {
        const L = await loadLeafletAssets()
        await this.$nextTick()
        if (!this.$refs.mapEl) return
        if (!this.map) {
          this.map = L.map(this.$refs.mapEl, { zoomControl: true })
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
          }).addTo(this.map)
        }
        if (this.layer) this.layer.clearLayers()
        else this.layer = L.featureGroup().addTo(this.map)
        this.shapes.forEach(shape => {
          L.polygon(shape.latlngs, {
            color: shape.color,
            weight: 2,
            fillColor: shape.color,
            fillOpacity: 0.28,
          }).bindPopup(shape.label).addTo(this.layer)
        })
        const bounds = this.layer.getBounds()
        if (bounds.isValid()) this.map.fitBounds(bounds.pad(0.18))
        this.$nextTick(() => {
          if (this.map) this.map.invalidateSize()
        })
      } catch (e) {
        this.error = 'تعذر عرض المضلع على الخريطة.'
      }
    },
  },
}
</script>
